<template>
  <Modal
    :modal-active="modelValue"
    has-header-border
    has-footer-border
    max-width="max-w-6xl"
    @close="close"
  >
    <template #header>
      <div class="flex flex-col gap-1 text-left">
        <h2 class="text-xl font-bold text-textPrimary">Avaliar registro</h2>
        <p v-if="queueTotal > 0" class="text-sm text-neutral50">
          Evidência {{ queuePosition }} de {{ queueTotal }}
          {{ queueTotal === 1 ? 'avaliação' : 'avaliações' }}
        </p>
      </div>
    </template>

    <div v-if="row" class="flex flex-col lg:flex-row gap-6 text-left">
      <div class="flex-1 min-w-0">
        <CheckEvidencePanel
          :check-id="row.checkId"
          @loaded="onEvidenceLoaded"
          @unavailable="emit('evidenceUnavailable', row.checkId)"
          @available="emit('evidenceAvailable', row.checkId)"
        />
      </div>

      <div class="w-full lg:w-[380px] shrink-0 flex flex-col gap-4">
        <CheckSummaryCard
          :row="row"
          :typed-hours="context ? { checkin: context.checkin, checkout: context.checkout } : null"
          :timezone="context?.timezone ?? ''"
          :doctor-name-override="context?.doctorName ?? null"
          :crm="context?.crm ?? null"
        />

        <div class="flex flex-col gap-2">
          <p class="text-base font-semibold text-textPrimary">Decisão</p>
          <label
            class="flex items-center gap-2 cursor-pointer"
            data-testid="check-validation-approve-radio"
          >
            <input
              v-model="decision"
              type="radio"
              value="APPROVE"
              class="w-5 h-5 accent-[#2f7f91]"
              data-testid="check-validation-approve-radio-input"
            />
            <span class="text-base text-textPrimary">Aprovar</span>
          </label>
          <label
            class="flex items-center gap-2 cursor-pointer"
            data-testid="check-validation-reject-radio"
          >
            <input
              v-model="decision"
              type="radio"
              value="REJECT"
              class="w-5 h-5 accent-[#2f7f91]"
              data-testid="check-validation-reject-radio-input"
            />
            <span class="text-base text-textPrimary">Reprovar</span>
          </label>
        </div>

        <template v-if="decision === 'REJECT'">
          <div class="flex flex-col gap-1.5">
            <v-select
              v-model="reason"
              :items="CHECK_REJECTION_REASON_OPTIONS"
              item-title="title"
              item-value="value"
              variant="outlined"
              density="comfortable"
              hide-details="auto"
              data-testid="check-validation-rejection-reason-select"
            >
              <template #label>
                Motivo da reprovação
                <span class="text-negative">*</span>
              </template>
            </v-select>
            <p class="text-xs text-neutral50">O motivo é registrado no histórico do registro.</p>
          </div>

          <div v-if="reason === CheckRejectionReason.OTHER" class="flex flex-col gap-1">
            <label
              class="text-sm font-bold text-textPrimary"
              for="check-validation-rejection-observation"
            >
              Observação <span class="text-negative">*</span>
            </label>
            <textarea
              id="check-validation-rejection-observation"
              v-model="observation"
              class="w-full border border-gray-300 rounded-lg p-3 text-sm text-textPrimary resize-none focus:outline-none focus:ring-2 focus:ring-primary30"
              rows="3"
              :placeholder="`Descreva o motivo (mín. ${CHECK_REJECTION_OBSERVATION_MIN_LENGTH} caracteres)`"
              data-testid="check-validation-rejection-observation-input"
            />
            <!-- Contador do texto ÚTIL (já com trim): é ele que o botão usa. -->
            <span
              class="self-end text-xs whitespace-nowrap"
              :class="isObservationValid ? 'text-neutral50' : 'text-negative'"
              data-testid="check-validation-rejection-char-counter"
            >
              {{ trimmedObservationLength }}/{{ CHECK_REJECTION_OBSERVATION_MIN_LENGTH }}
            </span>
          </div>
        </template>
      </div>
    </div>

    <template #footer>
      <div class="flex gap-4 w-full">
        <div class="flex-1">
          <OutlineButton
            text="Fechar"
            width="100%"
            height="48px"
            :disabled="saving"
            data-testid="check-validation-evaluate-cancel-button"
            @click="close"
          />
        </div>
        <div class="flex-1">
          <FilledButton
            :text="confirmLabel"
            width="100%"
            height="48px"
            :disabled="!canSubmit || saving"
            data-testid="check-validation-evaluate-confirm-button"
            @click="submit"
          />
        </div>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import Modal from '@/components/designSystem/modals/Modal.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import FilledButton from '@/components/designSystem/Buttons/FilledButton/index.vue'
import { CheckRejectionReason } from '@/Network/Types/Responses/CheckValidationResponses'
import {
  CHECK_REJECTION_OBSERVATION_MIN_LENGTH,
  CHECK_REJECTION_REASON_OPTIONS
} from '@/constants/check-validation.constants'

import CheckEvidencePanel from './CheckEvidencePanel.vue'
import type { CheckEvidenceContext } from './CheckEvidencePanel.vue'
import CheckSummaryCard from './CheckSummaryCard.vue'
import type { CheckValidationRow } from '../types'

export interface ApproveCheckPayload {
  checkId: number
  version: number
}

export interface RejectCheckModalPayload extends ApproveCheckPayload {
  reason: CheckRejectionReason
  observation?: string
}

type Decision = 'APPROVE' | 'REJECT' | ''

// Modal "burro": não chama Network. Emite a decisão e o pai (Index) fala com o
// store, toasta e decide fechar/recarregar — inclusive no 409.
const props = withDefaults(
  defineProps<{
    modelValue: boolean
    row: CheckValidationRow | null
    saving: boolean
    queuePosition?: number
    queueTotal?: number
  }>(),
  { queuePosition: 0, queueTotal: 0 }
)

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
  approve: [payload: ApproveCheckPayload]
  reject: [payload: RejectCheckModalPayload]
  evidenceUnavailable: [checkId: number]
  evidenceAvailable: [checkId: number]
}>()

const decision = ref<Decision>('')
const reason = ref<CheckRejectionReason | ''>('')
const observation = ref('')
const context = ref<CheckEvidenceContext | null>(null)

const trimmedObservationLength = computed(() => observation.value.trim().length)
const isObservationValid = computed(
  () => trimmedObservationLength.value >= CHECK_REJECTION_OBSERVATION_MIN_LENGTH
)

// Espelha o backend: motivo obrigatório na reprovação; observação (≥ 20) só em OTHER.
const canSubmit = computed(() => {
  if (!props.row) return false
  if (decision.value === 'APPROVE') return true
  if (decision.value !== 'REJECT') return false
  if (!reason.value) return false
  if (reason.value === CheckRejectionReason.OTHER) return isObservationValid.value
  return true
})

const confirmLabel = computed(() => {
  if (decision.value === 'APPROVE') return 'Aprovar registro'
  if (decision.value === 'REJECT') return 'Reprovar registro'
  return 'Confirmar'
})

function resetForm() {
  decision.value = ''
  reason.value = ''
  observation.value = ''
  context.value = null
}

// Nova linha ou reabertura: o formulário nunca reabre com a decisão anterior.
watch(
  () => [props.row?.checkId, props.modelValue],
  () => resetForm()
)

function onEvidenceLoaded(loaded: CheckEvidenceContext) {
  context.value = loaded
}

function submit() {
  if (!props.row || !canSubmit.value) return
  const base = { checkId: props.row.checkId, version: props.row.version }
  if (decision.value === 'APPROVE') {
    emit('approve', base)
    return
  }
  const payload: RejectCheckModalPayload = { ...base, reason: reason.value as CheckRejectionReason }
  if (reason.value === CheckRejectionReason.OTHER) payload.observation = observation.value.trim()
  emit('reject', payload)
}

// Não fecha com approve/reject em voo: o pai decide fechar no sucesso/409.
function close() {
  if (props.saving) return
  emit('update:modelValue', false)
}
</script>
